/**
 * Lean, hand-rolled markdown for agent prose (WS6). No dependency — the
 * agent's voice needs paragraphs, inline code (CommonMark backtick runs),
 * emphasis, ~~strikethrough~~, mini-headers, nested lists, GFM pipe tables
 * and fenced code blocks opened by ``` or ~~~ at a line start. Shared by
 * the transcript (MarkdownProse) and the export preview (exportPreview.tsx),
 * which reuses splitBlocks + renderBlock so both surfaces render at the same
 * quality.
 *
 * Inline code spans that look like entity references render as EntityChips
 * (hover pings the map, click focuses — DESIGN: "transcript and map are
 * never out of register"). Detection follows the frozen id grammar
 * (protocol/api.md §2): infra prefixes, `dir` paths, kebab flow slugs, and
 * `file.py:41` citations.
 */

import { Fragment, memo, useMemo, type CSSProperties, type ReactNode } from 'react';

import type { EntityRef } from '../../lib/frames';
import { EntityChip, FileChip } from './chips';
import { MermaidDiagram } from './MermaidDiagram';

export type CodeSpanMeaning =
  | { kind: 'entity'; ref: EntityRef }
  | { kind: 'file'; path: string; line: number | null }
  | { kind: 'code' };

const FILE_RE = /^([\w@.-]+(?:\/[\w@.-]+)*\.[a-z][a-z0-9]{0,5})(?::(\d+))?$/i;
const DIR_RE = /^[\w@.-]+(?:\/[\w@.-]+)+$/;
const FLOW_RE = /^[a-z0-9]+(?:-[a-z0-9]+)+$/;

/** Classify an inline code span per the entity-id grammar. */
export function classifyCodeSpan(raw: string): CodeSpanMeaning {
  const text = raw.trim();
  if (text.includes(' ') || text.includes('(')) return { kind: 'code' };
  if (text.startsWith('svc:')) return { kind: 'entity', ref: { kind: 'service', id: text } };
  if (text.startsWith('queue:')) return { kind: 'entity', ref: { kind: 'queue', id: text } };
  if (text.startsWith('sched:')) return { kind: 'entity', ref: { kind: 'schedule', id: text } };
  if (text.startsWith('ext:')) return { kind: 'entity', ref: { kind: 'external', id: text } };
  const file = FILE_RE.exec(text);
  if (file !== null && file[1] !== undefined) {
    return { kind: 'file', path: file[1], line: file[2] !== undefined ? Number(file[2]) : null };
  }
  if (DIR_RE.test(text)) return { kind: 'entity', ref: { kind: 'dir', id: text } };
  if (FLOW_RE.test(text)) return { kind: 'entity', ref: { kind: 'flow', id: text } };
  return { kind: 'code' };
}

/**
 * Bold-span classification (DESIGN2 §8.4, ux-audit #7: the model bolds
 * entity names — "**visibility-pipeline-run**" — and the chip parser only
 * looked at inline-code spans, so transcript↔map linking was lost).
 *
 * Bold is prose first: only spans that unambiguously match the entity-id
 * grammar become chips; everything else stays bold. Flow slugs are gated to
 * ≥3 kebab segments — English hyphenated compounds ("re-use",
 * "off-viewport") are single-hyphen, real slugged ids the model bolds are
 * longer. Returns null for "stay bold".
 */
export function classifyBoldSpan(raw: string): CodeSpanMeaning | null {
  const text = raw.trim();
  if (text.length === 0 || /\s/.test(text) || text.includes('(')) return null;
  const meaning = classifyCodeSpan(text);
  if (meaning.kind === 'code') return null;
  if (meaning.kind === 'entity' && meaning.ref.kind === 'flow') {
    const hyphens = (text.match(/-/g) ?? []).length;
    return hyphens >= 2 ? meaning : null;
  }
  return meaning;
}

function renderMeaning(meaning: CodeSpanMeaning, content: string, key: number): ReactNode {
  if (meaning.kind === 'entity') return <EntityChip key={key} entity={meaning.ref} />;
  if (meaning.kind === 'file') {
    return <FileChip key={key} path={meaning.path} line={meaning.line} label={content} />;
  }
  return (
    <code key={key} className="mono rounded-chip bg-inset px-1 py-0.5 text-secondary">
      {content}
    </code>
  );
}

function renderCodeSpan(content: string, key: number): ReactNode {
  return renderMeaning(classifyCodeSpan(content), content, key);
}

/**
 * Inline grammar. Code spans follow CommonMark backtick runs: an opener of
 * N backticks closes only on a run of exactly N, so a 4-run span holding a
 * ``` fence marker (real narration does this) stays INLINE code and never
 * bleeds. Strikethrough is DOUBLE tilde only — a single ~ stays literal so
 * approximations like "~40%" and "~100k → ~29k" survive untouched.
 */
const INLINE_RE =
  /(?<!`)(`+)(?!`)([^\n]*?)(?<!`)\1(?!`)|(\*\*[^*\n]+\*\*)|(\*[^*\n]+\*)|(~~[^~\n]+~~)/g;

/**
 * How deep emphasis may nest before the parser stops recursing and emits the
 * remaining text literally. Each level's content is strictly shorter than the
 * span that held it (the bold/italic/strike bodies exclude their own marker),
 * so recursion already terminates on its own; the cap is the second lock on
 * that door, and 4 is well past anything real prose does.
 */
const MAX_INLINE_DEPTH = 4;

/**
 * Emphasis is a SPAN, not a leaf (round 2 item 3): the guide writes
 * "**Six writers, one per `LevelSpec`**", and pushing the raw string into
 * <strong> rendered the backticks as literal characters. Bold, italic and
 * strikethrough all re-enter the inline parser on their contents so code
 * spans, chips and each other keep working inside them.
 */
function renderEmphasized(text: string, depth: number): ReactNode {
  return depth >= MAX_INLINE_DEPTH ? text : <>{renderInline(text, depth + 1)}</>;
}

/**
 * One paragraph's inline runs: code spans / bold / italic / strike / plain.
 * `depth` is internal (emphasis recursion); callers pass one argument.
 */
export function renderInline(text: string, depth = 0): ReactNode[] {
  const nodes: ReactNode[] = [];
  let cursor = 0;
  let key = 0;
  for (const match of text.matchAll(INLINE_RE)) {
    const index = match.index;
    if (index > cursor) {
      nodes.push(<Fragment key={key++}>{text.slice(cursor, index)}</Fragment>);
    }
    if (match[1] !== undefined) {
      // one space of padding is stripped when both sides carry it (the
      // CommonMark idiom for spans that start or end with a backtick)
      let content = match[2] ?? '';
      if (content.startsWith(' ') && content.endsWith(' ') && content.trim().length > 0) {
        content = content.slice(1, -1);
      }
      nodes.push(renderCodeSpan(content, key++));
    } else if (match[3] !== undefined) {
      // DESIGN2 §8.4 (ux-audit #7): bold spans matching the entity-id
      // grammar render as typed chips exactly like inline-code spans;
      // unmatched bold stays bold.
      const content = match[3].slice(2, -2);
      const meaning = classifyBoldSpan(content);
      if (meaning !== null) {
        nodes.push(renderMeaning(meaning, content.trim(), key++));
      } else {
        nodes.push(<strong key={key++}>{renderEmphasized(content, depth)}</strong>);
      }
    } else if (match[4] !== undefined) {
      nodes.push(<em key={key++}>{renderEmphasized(match[4].slice(1, -1), depth)}</em>);
    } else {
      nodes.push(<del key={key++}>{renderEmphasized((match[5] ?? '').slice(2, -2), depth)}</del>);
    }
    cursor = index + match[0].length;
  }
  if (cursor < text.length) {
    nodes.push(<Fragment key={key++}>{text.slice(cursor)}</Fragment>);
  }
  return nodes;
}

export interface MdListItem {
  text: string;
  children: MdListItem[];
  /** whether the nested list under this item is ordered */
  childOrdered: boolean;
}

export type MdBlock =
  | { kind: 'paragraph'; text: string }
  /** `open`: the fence had no closing run, so it is still streaming in (or was never closed) */
  | { kind: 'code'; text: string; lang: string | null; open?: true }
  | { kind: 'heading'; level: number; text: string }
  | { kind: 'list'; ordered: boolean; items: MdListItem[] }
  | {
      kind: 'table';
      header: string[];
      align: Array<'left' | 'center' | 'right' | null>;
      rows: string[][];
    };

/**
 * A code span alone (the inline grammar's first branch), for the block
 * splitter's JSON promotion below.
 */
const CODE_SPAN_RE = /(?<!`)(`+)(?!`)([^\n]*?)(?<!`)\1(?!`)/g;

/**
 * Round 4 (owner): "Why isn't the json/dict formatted in the chat? It's not
 * very readable." The model had written a whole record inline, and a
 * `{brand: "Acme", query: "...", cited_sources: [...]}` run wrapped across
 * three lines of prose as one unbroken grey smear.
 *
 * Fenced blocks already render properly; the bug is objects arriving INLINE.
 * So an inline span that is really a record gets promoted to a block: the
 * paragraph splits around it and it renders as the fence the model should have
 * written. Two triggers, both conservative:
 *  - it parses as JSON and holds something (an empty `{}` is not a document),
 *  - or it is shaped like an object/array literal and long enough to be the
 *    run-on the owner saw. Python-style dicts with unquoted keys never parse,
 *    and they are exactly the case that was reported.
 *
 * Anything else stays inline, so `{}`, `[1, 2]` and `enqueue()` are untouched.
 */
const JSON_INLINE_MIN_LENGTH = 60;

function looksLikeRecord(text: string): boolean {
  return (
    (text.startsWith('{') && text.endsWith('}')) || (text.startsWith('[') && text.endsWith(']'))
  );
}

/**
 * Pretty-print an object/array literal that JSON cannot parse (unquoted keys,
 * single quotes, an elided `[...]`): break after the commas that sit at the
 * top level and indent one step. String contents are stepped over, so a comma
 * inside a value never becomes a line break.
 */
function formatRecordLiteral(text: string): string {
  const out: string[] = [];
  let depth = 0;
  let quote: string | null = null;
  const indent = () => '  '.repeat(Math.max(0, depth));
  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i]!;
    if (quote !== null) {
      out.push(ch);
      if (ch === '\\') {
        const next = text[i + 1];
        if (next !== undefined) {
          out.push(next);
          i += 1;
        }
      } else if (ch === quote) {
        quote = null;
      }
      continue;
    }
    if (ch === '"' || ch === "'") {
      quote = ch;
      out.push(ch);
      continue;
    }
    if (ch === '{' || ch === '[') {
      depth += 1;
      out.push(ch, '\n', indent());
      continue;
    }
    if (ch === '}' || ch === ']') {
      depth -= 1;
      out.push('\n', indent(), ch);
      continue;
    }
    if (ch === ',') {
      out.push(ch, '\n', indent());
      // the space that followed the comma is now the indent
      if (text[i + 1] === ' ') i += 1;
      continue;
    }
    out.push(ch);
  }
  return out
    .join('')
    .split('\n')
    .map((line) => line.replace(/\s+$/, ''))
    .filter((line) => line.length > 0)
    .join('\n')
    .trim();
}

/**
 * The pretty-printed block this inline code span should become, or null when
 * it should stay inline. Exported for tests.
 */
export function jsonBlockForCodeSpan(raw: string): string | null {
  const text = raw.trim();
  if (!looksLikeRecord(text)) return null;
  try {
    const parsed: unknown = JSON.parse(text);
    if (typeof parsed === 'object' && parsed !== null) {
      const empty = Array.isArray(parsed) ? parsed.length === 0 : Object.keys(parsed).length === 0;
      if (!empty) return JSON.stringify(parsed, null, 2);
    }
  } catch {
    // not JSON: fall through to the literal path
  }
  return text.length > JSON_INLINE_MIN_LENGTH ? formatRecordLiteral(text) : null;
}

/**
 * One paragraph, split around any record the model wrote inline: prose, the
 * record as a json block, prose. A paragraph with none comes back whole.
 */
function splitRecordSpans(text: string): MdBlock[] {
  const blocks: MdBlock[] = [];
  let cursor = 0;
  for (const match of text.matchAll(CODE_SPAN_RE)) {
    const pretty = jsonBlockForCodeSpan(match[2] ?? '');
    if (pretty === null) continue;
    const before = text.slice(cursor, match.index).trim();
    if (before.length > 0) blocks.push({ kind: 'paragraph', text: before });
    blocks.push({ kind: 'code', text: pretty, lang: 'json' });
    cursor = match.index + match[0].length;
  }
  if (blocks.length === 0) return [{ kind: 'paragraph', text }];
  const after = text.slice(cursor).trim();
  if (after.length > 0) blocks.push({ kind: 'paragraph', text: after });
  return blocks;
}

const HEADING_RE = /^(#{1,6})\s+(.*?)\s*#*\s*$/;
const LIST_ITEM_RE = /^(\s*)([-*+]|\d+[.)])\s+(.*)$/;
// Fences open ONLY at a line start (CommonMark), with either character; a
// backtick fence's info string may not contain a backtick — that is how the
// narrator writes inline spans like `````` ```chart `````` without opening
// a block. The close run must match the character and be at least as long.
const FENCE_OPEN_RE = /^ {0,3}(`{3,}|~{3,})(.*)$/;
const FENCE_CLOSE_RE = /^ {0,3}(`{3,}|~{3,})\s*$/;
const TABLE_DELIM_CELL_RE = /^:?-+:?$/;

/** "| a | b |" → ["a", "b"] — outer pipes optional, cells trimmed. */
function splitCells(line: string): string[] {
  let s = line.trim();
  if (s.startsWith('|')) s = s.slice(1);
  if (s.endsWith('|')) s = s.slice(0, -1);
  return s.split('|').map((c) => c.trim());
}

function tableAlign(cell: string): 'left' | 'center' | 'right' | null {
  const left = cell.startsWith(':');
  const right = cell.endsWith(':');
  if (left && right) return 'center';
  if (right) return 'right';
  if (left) return 'left';
  return null;
}

/**
 * Block-split agent prose: paragraphs, fenced code (``` or ~~~, unclosed
 * fences run to the end so streaming stays stable), headings (mini-headers,
 * DESIGN2-era voice rules), GFM pipe tables (a header row directly over a
 * delimiter row), and bullet/numbered lists with one nesting level per 2+
 * spaces of indent. Anything unrecognized stays a paragraph.
 */
export function splitBlocks(text: string): MdBlock[] {
  const blocks: MdBlock[] = [];
  const lines = text.split('\n');
  let i = 0;
  let para: string[] = [];
  const flushPara = () => {
    const joined = para.join(' ').trim();
    if (joined.length > 0) blocks.push(...splitRecordSpans(joined));
    para = [];
  };

  while (i < lines.length) {
    const line = lines[i] ?? '';

    const fence = FENCE_OPEN_RE.exec(line);
    if (fence !== null && !(fence[1]![0] === '`' && (fence[2] ?? '').includes('`'))) {
      flushPara();
      const marker = fence[1]!;
      // The info string's first word is the language ("mermaid" routes to
      // diagram rendering); it never appears inside the block.
      const info = (fence[2] ?? '').trim().split(/\s+/)[0] ?? '';
      const body: string[] = [];
      let closed = false;
      i += 1;
      while (i < lines.length) {
        const l = lines[i] ?? '';
        const close = FENCE_CLOSE_RE.exec(l);
        if (close !== null && close[1]![0] === marker[0] && close[1]!.length >= marker.length) {
          closed = true;
          i += 1;
          break;
        }
        body.push(l);
        i += 1;
      }
      blocks.push({
        kind: 'code',
        text: body.join('\n'),
        lang: info.length > 0 ? info.toLowerCase() : null,
        ...(closed ? {} : { open: true as const }),
      });
      continue;
    }

    if (line.trim().length === 0) {
      flushPara();
      i += 1;
      continue;
    }

    const heading = HEADING_RE.exec(line);
    if (heading !== null) {
      flushPara();
      blocks.push({ kind: 'heading', level: heading[1]!.length, text: heading[2] ?? '' });
      i += 1;
      continue;
    }

    // GFM pipe table: this line is the header only when the NEXT line is a
    // well-formed delimiter row of the same width — a lone "|" in prose
    // never becomes a table.
    if (line.includes('|')) {
      const delim = lines[i + 1];
      if (delim !== undefined && delim.includes('|') && delim.includes('-')) {
        const header = splitCells(line);
        const delimCells = splitCells(delim);
        if (
          delimCells.length === header.length &&
          delimCells.every((c) => TABLE_DELIM_CELL_RE.test(c))
        ) {
          flushPara();
          const align = delimCells.map(tableAlign);
          const rows: string[][] = [];
          i += 2;
          while (i < lines.length) {
            const l = lines[i] ?? '';
            if (!l.includes('|') || l.trim().length === 0) break;
            const cells = splitCells(l);
            rows.push(header.map((_, c) => cells[c] ?? ''));
            i += 1;
          }
          blocks.push({ kind: 'table', header, align, rows });
          continue;
        }
      }
    }

    const first = LIST_ITEM_RE.exec(line);
    if (first !== null) {
      flushPara();
      const ordered = /\d/.test(first[2] ?? '');
      const root: MdListItem[] = [];
      const stack: Array<{ indent: number; items: MdListItem[] }> = [
        { indent: (first[1] ?? '').length, items: root },
      ];
      while (i < lines.length) {
        const l = lines[i] ?? '';
        const m = LIST_ITEM_RE.exec(l);
        if (m === null) {
          // an indented continuation line extends the previous item
          const top = stack[stack.length - 1]!;
          const last = top.items[top.items.length - 1];
          if (l.trim().length > 0 && /^\s{2,}/.test(l) && last !== undefined) {
            last.text += ` ${l.trim()}`;
            i += 1;
            continue;
          }
          break;
        }
        const indent = (m[1] ?? '').length;
        while (stack.length > 1 && indent < stack[stack.length - 1]!.indent) {
          stack.pop();
        }
        const top = stack[stack.length - 1]!;
        if (indent > top.indent) {
          const last = top.items[top.items.length - 1];
          if (last !== undefined) {
            last.childOrdered = /\d/.test(m[2] ?? '');
            stack.push({ indent, items: last.children });
          }
        }
        stack[stack.length - 1]!.items.push({
          text: m[3] ?? '',
          children: [],
          childOrdered: false,
        });
        i += 1;
      }
      blocks.push({ kind: 'list', ordered, items: root });
      continue;
    }

    para.push(line.trim());
    i += 1;
  }
  flushPara();
  return blocks;
}

/**
 * Agent prose block: the system sans stack (--font-prose) at the fixed
 * transcript scale, code in JetBrains Mono. Sizing lives in
 * `.lm-transcript-prose` (index.css): the font size is FIXED at every panel
 * width and the prose fills the full chat width minus the column padding
 * (owner 2026-08-25 items 1-2; a wide panel means longer lines, never
 * bigger type).
 *
 * Memoized both ways for the streaming path: a delta re-renders the
 * transcript dozens of times a second, and only the LAST prose block's text
 * actually changes — every finished turn's MarkdownProse skips on
 * `React.memo`, and the one that did change re-splits its blocks exactly
 * once (`useMemo` on the text).
 */
export const MarkdownProse = memo(function MarkdownProse({ text }: { text: string }) {
  const blocks = useMemo(() => splitBlocks(text), [text]);
  return (
    <div className="lm-transcript-prose">{blocks.map((block, i) => renderBlock(block, i))}</div>
  );
});

function renderListItems(items: MdListItem[], ordered: boolean, nested: boolean): ReactNode {
  const cls = `${nested ? 'mt-1 pl-4' : 'my-2 pl-5 first:mt-0 last:mb-0'} space-y-1 ${
    ordered ? 'list-decimal' : 'list-disc'
  }`;
  const rows = items.map((item, i) => (
    <li key={i}>
      {renderInline(item.text)}
      {item.children.length > 0 ? renderListItems(item.children, item.childOrdered, true) : null}
    </li>
  ));
  return ordered ? <ol className={cls}>{rows}</ol> : <ul className={cls}>{rows}</ul>;
}

function alignStyle(
  align: 'left' | 'center' | 'right' | null | undefined,
): CSSProperties | undefined {
  return align === null || align === undefined ? undefined : { textAlign: align };
}

/** How a column is drawn: hugging its own text, or dividing the remainder. */
export type MdTableColumn = 'hug' | 'flow';

/**
 * The widest cell a column may hold and still be read as a LABEL column.
 * About two short words: past that it is prose, and prose wraps.
 */
export const TABLE_HUG_MAX_CHARS = 24;

/**
 * What every hugging column may claim between them. Three little columns can
 * all hug; two near the limit cannot both pin a table open in a chat column
 * that is only ~460px wide.
 */
export const TABLE_HUG_BUDGET_CHARS = 40;

/** Roughly how many characters a cell puts on a line (markup is not text). */
function cellWidth(cell: string): number {
  return cell.replace(/[`*~_]/g, '').trim().length;
}

/**
 * Which columns hug their content and which take the space that is left.
 *
 * Round 7 (owner): "The table column widths can be better drawn dynamically."
 * In a real answer a two-column table rendered with "Receipt check" and
 * "Self-contradiction set" wrapping onto two and three lines, against a very
 * wide prose column beside them. That is exactly what `table-layout: auto`
 * does inside a full-width table when it has to squeeze: it hands the space
 * out in proportion to each column's MAX content, so the column holding a
 * paragraph takes nearly all of it and the column holding two words is
 * chopped to fit.
 *
 * So the renderer says which columns are labels, and the sheet lets those hug
 * (`width: 1%` with `white-space: nowrap`, which is the honest version of a
 * hand-tuned min-width: the minimum IS the text). Nothing is a fixed
 * percentage, so the same table with longer labels simply stops hugging.
 *
 * A column hugs only when its widest cell is short, only while the shared
 * budget lasts (spent narrowest-first, so the cheapest labels win it), and
 * only if some other column is left to take the remainder: a table whose
 * columns are ALL short is left to the browser, which spreads it evenly.
 */
export function tableColumnLayout(
  header: readonly string[],
  rows: readonly (readonly string[])[],
): MdTableColumn[] {
  const widths = header.map((cell, c) =>
    rows.reduce((widest, row) => Math.max(widest, cellWidth(row[c] ?? '')), cellWidth(cell)),
  );
  const layout: MdTableColumn[] = widths.map(() => 'flow');
  const candidates = widths
    .map((width, index) => ({ width, index }))
    .filter((c) => c.width <= TABLE_HUG_MAX_CHARS)
    .sort((a, b) => a.width - b.width || a.index - b.index);
  if (candidates.length === widths.length) return layout;

  let budget = TABLE_HUG_BUDGET_CHARS;
  for (const { width, index } of candidates) {
    if (width > budget) continue;
    budget -= width;
    layout[index] = 'hug';
  }
  return layout;
}

/**
 * One block → element. Exported for the export preview (exportPreview.tsx),
 * which wraps the same blocks in its own typography.
 */
export function renderBlock(block: MdBlock, key: number): ReactNode {
  switch (block.kind) {
    case 'code':
      // ```mermaid fences render as diagrams (lazy-loaded, token-themed,
      // error-safe — a bad diagram falls back to the plain code block
      // below). A fence still open is still streaming in: it is drawn after
      // a pause in its deltas, and at once when it closes.
      if (block.lang === 'mermaid') {
        return <MermaidDiagram key={key} code={block.text} streaming={block.open === true} />;
      }
      return (
        <pre
          key={key}
          data-lang={block.lang ?? undefined}
          // Data wraps; code scrolls. A record is read field by field, and a
          // 460px transcript column is too narrow to make a reader drag a
          // horizontal scrollbar to finish a value.
          className={`mono my-3 overflow-x-auto rounded-card border border-default bg-inset p-3 ${
            block.lang === 'json' ? 'lm-code-wrap' : ''
          }`}
        >
          {block.text}
        </pre>
      );
    case 'heading':
      // Typographic hierarchy: # is the section tier (tokens' section
      // scale), ## the semibold prose header, ###+ the app's micro
      // section-label idiom — three distinct, learnable levels.
      return block.level === 1 ? (
        <h2
          key={key}
          className="mt-5 mb-2 text-[length:var(--text-section-size)] leading-[var(--text-section-line)] font-semibold text-primary first:mt-0"
        >
          {renderInline(block.text)}
        </h2>
      ) : block.level === 2 ? (
        <h3 key={key} className="mt-4 mb-1 font-semibold text-primary first:mt-0">
          {renderInline(block.text)}
        </h3>
      ) : (
        <h4
          key={key}
          className="mt-3 mb-1 text-[length:var(--text-micro-size)] leading-[var(--text-micro-line)] font-medium tracking-[var(--text-micro-tracking)] uppercase text-tertiary first:mt-0"
          style={{ fontFamily: 'var(--font-ui)' }}
        >
          {renderInline(block.text)}
        </h4>
      );
    case 'list':
      return <Fragment key={key}>{renderListItems(block.items, block.ordered, false)}</Fragment>;
    case 'table': {
      // Which columns hug and which flow, decided from the content itself
      // (tableColumnLayout); the sheet turns the answer into widths.
      const layout = tableColumnLayout(block.header, block.rows);
      return (
        <div key={key} className="my-3 overflow-x-auto first:mt-0 last:mb-0">
          <table
            className="lm-md-table border-collapse"
            style={{
              fontFamily: 'var(--font-ui)',
              fontSize: 'var(--text-ui-size)',
              lineHeight: 'var(--text-ui-line)',
            }}
          >
            <thead>
              <tr>
                {block.header.map((cell, c) => (
                  <th
                    key={c}
                    data-col={layout[c] ?? 'flow'}
                    className="border border-default bg-inset px-2 py-1 text-left font-semibold"
                    style={alignStyle(block.align[c])}
                  >
                    {renderInline(cell)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row, r) => (
                <tr key={r}>
                  {row.map((cell, c) => (
                    <td
                      key={c}
                      data-col={layout[c] ?? 'flow'}
                      className="border border-default px-2 py-1 align-top"
                      style={alignStyle(block.align[c])}
                    >
                      {renderInline(cell)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }
    case 'paragraph':
      return (
        <p key={key} className="my-2 first:mt-0 last:mb-0">
          {renderInline(block.text)}
        </p>
      );
  }
}
